import { useEffect, useState } from 'react'
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { useCurrentSelf } from '../../../hooks/auth/useCurrentSelf'
import { getClockStatus, punchClock, type ClockKind, type ClockStatus } from '../../../api/time_clock/requests'

export function useClockIdentity() {
  const { data } = useCurrentSelf()
  return data?.employeeId || ''
}
export function useClockStatus(enabled = true) {
  const identity = useClockIdentity()
  return useQuery({
    queryKey: ['time-clock', identity, 'status'],
    queryFn: getClockStatus,
    enabled: enabled && !!identity,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    retry: 1,
    staleTime: 5000,
  })
}
export function useClockMutation<T>(mutationFn: (value: T) => Promise<unknown>, success = 'saved') {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()
  return useMutation({
    mutationKey: ['time-clock-write'],
    mutationFn,
    onSuccess: () => {
      toast({ title: t(`timeClock.${success}`), status: 'success', duration: 2500, isClosable: true })
    },
    onError: (error) => {
      const code = isAxiosError(error) ? error.response?.data?.code : undefined
      toast({
        title: t('timeClock.failed'),
        description: code ? t(`timeClock.errorCodes.${code}`, { defaultValue: '' }) : undefined,
        status: 'error',
        duration: 7000,
        isClosable: true,
      })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['time-clock'] })
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-notifications'] })
    },
  })
}
export function usePunch(status?: ClockStatus) {
  const busy = useIsMutating({ mutationKey: ['time-clock-write'] }) > 0
  const mutation = useClockMutation(async (kind: ClockKind) => {
    const payload: Parameters<typeof punchClock>[0] = { kind, request_id: crypto.randomUUID() }
    if (status?.geofence === 'flag' && (kind === 'in' || kind === 'out') && navigator.geolocation) {
      await new Promise<void>((resolve) =>
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            Object.assign(payload, {
              latitude: coords.latitude,
              longitude: coords.longitude,
              accuracy: coords.accuracy,
            })
            resolve()
          },
          () => resolve(),
          { timeout: 7000, maximumAge: 0, enableHighAccuracy: true },
        ),
      )
    }
    // Reuse the same request ID after a lost response. Never retry a rejected clock transition.
    try {
      return await punchClock(payload)
    } catch (error) {
      if (isAxiosError(error) && !error.response) return punchClock(payload)
      throw error
    }
  }, 'recorded')
  return { ...mutation, busy }
}
export function useLiveSeconds(status: ClockStatus | undefined, receivedAt: number) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!status || status.state === 'off') return
    const handle = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(handle)
  }, [status?.state])
  const elapsed = Math.max(0, (now - receivedAt) / 1000)
  return {
    worked: (status?.worked_seconds || 0) + (status?.state === 'working' ? elapsed : 0),
    paused:
      status?.state === 'paused' && status.since
        ? Math.max(0, (Date.parse(status.server_now) - Date.parse(status.since)) / 1000 + elapsed)
        : 0,
  }
}
